import type { BackendErrorEntity, ErrorType } from '~/apiClient';

export interface UseWatchQueryErrorParams<T> {
    data: Ref<T>;
    error: Ref<ErrorType<BackendErrorEntity> | null>;
    logMessage: string;
    logContext?: MaybeRefOrGetter<Record<string, unknown>>;
}

/**
 * Takes over the page with an error only when there is nothing left to render, deriving the error
 * page from the response itself: the server failed (5xx), or it understood the request and refused
 * it (anything else, which for a page load means the URL leads nowhere).
 *
 * A query keeps its previous data when a refetch fails (TanStack sets `status: 'error'` without
 * dropping `data`), so a failed background refetch must not destroy a working view. Uses
 * `watchEffect` to also cover the initial fetch, whether called before or after `await suspense()`.
 */
export const useWatchQueryError = <T>({ data, error, logMessage, logContext }: UseWatchQueryErrorParams<T>): void => {
    // COMPOSABLES
    const { isSessionExpired } = useSessionExpiration();
    const { logError } = useLogs();

    // WATCHERS
    watchEffect(() => {
        // A query holds an error only while its status is 'error', so this covers `isError` too.
        const fetchError = error.value;

        if (!fetchError || isSessionExpired(fetchError.data?.error.code)) return;

        const { statusCode } = fetchError;

        // No HTTP response came back: a connectivity failure or an aborted request. Neither is a
        // backend fault, so the page is left as it is — and neither is logged, because on an
        // unstable network that would flood Sentry with events that say nothing about the backend.
        if (statusCode === undefined) return;

        logError(logMessage, { error: fetchError, statusCode, ...toValue(logContext) });

        if (data.value) return;

        showError({ statusCode: statusCode >= 500 ? 500 : 404, statusMessage: logMessage });
    });
};
